import type { Store } from "@redotech/redo-partners-portal-rpc/schema/shared/store";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { usePartnersPortalRpcClient } from "../rpc/client-context";

type CreateStoreInput = Parameters<
  ReturnType<typeof usePartnersPortalRpcClient>["createStore"]
>[0];

const storesKey = ["stores"] as const;
const storeKey = (storeId: string) => ["stores", storeId] as const;
const storeTokensKey = (storeId: string) =>
  ["stores", storeId, "api-tokens"] as const;

export function useStores() {
  const rpc = usePartnersPortalRpcClient();
  return useQuery({
    queryKey: storesKey,
    queryFn: async () => (await rpc.listStores({})).stores,
  });
}

export function useStore(storeId: string) {
  const rpc = usePartnersPortalRpcClient();
  return useQuery({
    queryKey: storeKey(storeId),
    queryFn: async () => (await rpc.getStore({ storeId })).store,
  });
}

export function useStoreApiTokens(storeId: string) {
  const rpc = usePartnersPortalRpcClient();
  return useQuery({
    queryKey: storeTokensKey(storeId),
    queryFn: async () => (await rpc.listStoreApiTokens({ storeId })).tokens,
  });
}

export function useCreateStore(): (input: CreateStoreInput) => Promise<Store> {
  const rpc = usePartnersPortalRpcClient();
  const queryClient = useQueryClient();
  return async (input) => {
    const { store } = await rpc.createStore(input);
    await queryClient.invalidateQueries({ queryKey: storesKey });
    return store;
  };
}

export function useStoreTokenActions(storeId: string) {
  const rpc = usePartnersPortalRpcClient();
  const queryClient = useQueryClient();
  const refresh = () =>
    queryClient.invalidateQueries({ queryKey: storeTokensKey(storeId) });
  return {
    create: async (input: { name: string; scopes: Store["scopes"] }) => {
      const { secret } = await rpc.createStoreApiToken({ storeId, ...input });
      await refresh();
      return secret;
    },
    revoke: async (tokenId: string) => {
      await rpc.revokeStoreApiToken({ storeId, tokenId });
      await refresh();
    },
  };
}
